import clsx from "clsx";
import { memo, useCallback } from "react";
import { useFormStatus } from "react-dom";
import { z } from "zod";
import { Icon, type IconProps } from "@/ui/icon/Icon";
import styles from "./Btn.module.css";

export const BtnSizeSchema = z.literal(["lg", "md", "sm", "xs", "2xs"]);

type BtnSize = z.infer<typeof BtnSizeSchema>;

export const BtnColorSchema = z.enum([
  "ghost",
  "ghost-white",
  "stroke",
  "stroke-white",
  "stroke-dark",
  "white",
  "grey",
  "primary",
  "primary-stroke",
  "primary-light",
  "primary-light-stroke",
  "dark",
  "dark-stroke",
  "orange",
  "orange-stroke",
  "green",
  "green-stroke",
  "green-light",
  "green-light-stroke",
  "red",
]);

export type BtnColor = z.infer<typeof BtnColorSchema>;

export const BtnModeSchema = z.enum([
  "none",
  "remove",
  "close",
  "clear",
  "cancel",
  "delete",
  "danger",
  "confirm",
  "youtube",
  "black",
  "grey",
  "green",
]);

export type BtnMode = z.infer<typeof BtnModeSchema>;

export const BtnTypeSchema = z.enum(["button", "submit", "reset", "select"]);

export type BtnType = z.infer<typeof BtnTypeSchema>;

interface BtnTextItem {
  type: "text";
  value: string;
}

interface BtnIconItem {
  type: "icon";
  value: IconProps["name"] | IconProps;
}

interface BtnImage {
  src: string;
  size?: number;
  width?: number;
  height?: number;
  rounded?: boolean;
  className?: string;
}

type BtnItem = BtnTextItem | BtnIconItem;

type BtnText = string | number | React.ReactElement;

export interface BtnBaseProps extends React.HTMLAttributes<HTMLElement> {
  form?: string;
  text?: BtnText;
  icon?: IconProps["name"] | IconProps | BtnImage;
  items?: BtnItem[];
  endIcon?: IconProps["name"] | IconProps | BtnImage;
  size?: BtnSize;
  md?: BtnSize;
  xl?: BtnSize;
  color?: BtnColor;
  mode?: BtnMode;
  fab?: boolean;
  active?: boolean;
  stretch?: boolean;
  disabled?: boolean;
  loading?: boolean;
  isolate?: boolean;
  tabIndex?: number;
  href?: string;
  target?: "_self" | "_blank" | "_parent" | "_top" | "_unfencedTop";
  className?: string;
  onClick?: (event: React.MouseEvent) => void;
}

export interface BtnButtonProps extends BtnBaseProps {
  type: BtnType;
  href?: never;
  htmlFor?: never;
  ref?: React.Ref<HTMLButtonElement>;
}

export interface BtnAnchorProps extends BtnBaseProps {
  type?: never;
  href: string | undefined;
  htmlFor?: never;
  ref?: React.Ref<HTMLAnchorElement>;
}

export interface BtnLabelProps extends BtnBaseProps {
  type?: never;
  href?: never;
  htmlFor?: string;
  ref?: React.Ref<HTMLLabelElement>;
}

export interface BtnDivProps extends BtnBaseProps {
  type?: never;
  href?: never;
  htmlFor?: never;
  ref?: React.Ref<HTMLDivElement>;
}

export type BtnProps =
  | BtnDivProps
  | BtnButtonProps
  | BtnAnchorProps
  | BtnLabelProps;

export interface BtnComponent<T extends object = {}> {
  (props: BtnDivProps & T): React.ReactNode;
  (props: BtnButtonProps & T): React.ReactNode;
  (props: BtnAnchorProps & T): React.ReactNode;
  (props: BtnLabelProps & T): React.ReactNode;
  (props: BtnProps & T): React.ReactNode;
}

export const Btn: BtnComponent = memo(
  ({
    type,
    htmlFor,
    form,
    text,
    icon,
    endIcon,
    items,
    size = "md",
    md,
    xl,
    color = "ghost",
    mode = "none",
    fab = false,
    active = false,
    stretch = false,
    disabled = false,
    isolate = false,
    tabIndex,
    href,
    loading: loadingProp = false,
    target,
    className: classNameProp,
    onClick,
    ref,
    ...props
  }) => {
    const { pending } = useFormStatus();

    const loading = (type === "submit" && pending) || loadingProp;

    const className = clsx(
      styles.btn,
      type !== undefined && styles[`btn-type-${type}`],
      styles[`btn-color-${color}`],
      styles[`btn-size-${size}`],
      md !== undefined && styles[`btn-device-md-size-${md}`],
      xl !== undefined && styles[`btn-device-xl-size-${xl}`],
      styles[`btn-mode-${mode}`],
      fab && styles[`btn-fab`],
      active && styles.active,
      stretch && styles[`btn-stretch`],
      disabled && styles[`btn-disabled`],
      loading && styles[`btn-loading`],
      classNameProp,
    );

    const onClickHandler = useCallback(
      (event: React.MouseEvent) => {
        if (isolate === true) {
          event.preventDefault();
          event.stopPropagation();
        }

        if ((type === "submit" && loading === true) || disabled === true) {
          event.preventDefault();
        }

        if (loading === false && disabled === false) {
          onClick?.(event);
        }
      },
      [disabled, isolate, loading, onClick, type],
    );

    if (href !== undefined) {
      return (
        <a
          ref={ref}
          className={className}
          href={href}
          target={target}
          rel={target !== undefined ? "nofollow" : undefined}
          tabIndex={tabIndex}
          onClick={onClickHandler}
          {...props}
        >
          <BtnContent
            icon={icon}
            endIcon={endIcon}
            text={text}
            loading={loading}
          />
        </a>
      );
    }

    if (type === "button" || type === "submit" || type === "reset") {
      return (
        <button
          ref={ref}
          className={className}
          type={type}
          form={form}
          tabIndex={tabIndex}
          onClick={onClickHandler}
          {...props}
        >
          <BtnContent
            icon={icon}
            endIcon={endIcon}
            text={text}
            loading={loading}
          />
        </button>
      );
    }

    if (htmlFor !== undefined) {
      return (
        <label
          ref={ref}
          htmlFor={htmlFor}
          className={className}
          tabIndex={tabIndex}
          onClick={onClickHandler}
          {...props}
        >
          <BtnContent
            icon={icon}
            endIcon={endIcon}
            text={text}
            loading={loading}
          />
        </label>
      );
    }

    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={className}
        tabIndex={tabIndex}
        onClick={onClickHandler}
        {...props}
      >
        <BtnContent
          icon={icon}
          endIcon={endIcon}
          text={text}
          items={items}
          loading={loading}
        />
      </div>
    );
  },
);

const BtnContent: React.FC<BtnProps> = ({
  icon,
  endIcon,
  text,
  items,
  loading,
}) => {
  return (
    <>
      {icon !== undefined &&
        (loading === true ? (
          <BtnLoadingIcon
            className={clsx(styles["btn-icon"], styles["btn-start-icon"])}
          />
        ) : (
          <BtnIcon
            className={clsx(styles["btn-icon"], styles["btn-start-icon"])}
            icon={icon}
          />
        ))}
      {text !== undefined && <BtnText text={text} />}
      {items?.map((item, index) => {
        switch (item.type) {
          case "text":
            return <BtnText key={index} text={item.value} />;
          case "icon":
            return <BtnIcon key={index} icon={item.value} />;
        }
      })}
      {endIcon !== undefined &&
        (loading === true ? (
          <BtnLoadingIcon
            className={clsx(styles["btn-icon"], styles["btn-end-icon"])}
          />
        ) : (
          <BtnIcon
            className={clsx(styles["btn-icon"], styles["btn-end-icon"])}
            icon={endIcon}
          />
        ))}
    </>
  );
};

const BtnText: React.FC<{
  text: BtnText;
}> = ({ text }) => {
  return <div className={styles["btn-text"]}>{text}</div>;
};

const BtnIcon: React.FC<{
  icon: IconProps["name"] | IconProps | BtnImage;
  className?: string;
}> = ({ icon, className }) => {
  if (typeof icon === "string") {
    return <Icon className={className} name={icon} />;
  }

  if ("src" in icon) {
    const finalWidth = icon.size ?? icon.width ?? 24;
    const finalHeight = icon.size ?? icon.height ?? finalWidth;

    return (
      <img
        className={clsx(
          className,
          icon.rounded && styles["btn-icon-rounded"],
          icon.className,
        )}
        src={icon.src}
        width={finalWidth}
        height={finalHeight}
        alt=""
      />
    );
  }

  return <Icon {...icon} className={clsx(className, icon.className)} />;
};

const BtnLoadingIcon: React.FC<{
  className?: string;
}> = ({ className }) => {
  return (
    <Icon className={className} name="loader_24px_regular" animation="spin" />
  );
};
